import { Button, Empty, Switch, Tag } from 'antd'
import {
  ApiOutlined,
  BarChartOutlined,
  BellOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ControlOutlined,
  ControlOutlined as GearOutlined,
  DeleteOutlined,
  FileTextOutlined,
  LeftOutlined,
  PlusOutlined,
  StarOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useAssistant } from '../AssistantProvider'
import { popupTargetRoute } from '../popupBehaviour'
import { isWelcomePopup, type PopupEventKey } from '../types'

dayjs.extend(relativeTime)

/**
 * The history of what has already popped.
 *
 * Every trigger pops in the corner as it fires — this tab is where they land
 * afterwards. Each entry is a headline, a sentence and a timestamp; clicking an
 * unread one marks it read, and the bin removes it from the feed for good. The
 * first-run message is the single exception, because offering a tour is its
 * whole point.
 */

/**
 * The server sends no icon — restyling stays a frontend change (spec §3.1), so
 * every one of the twenty-five events maps to one here.
 *
 * Eight of the messages also open with an emoji, which is part of the approved
 * copy and arrives inside `body` (spec §12). That is not a duplicate of this
 * map and neither replaces the other: the icon is furniture, identifying the
 * event at a glance down the left of every row, and the emoji is punctuation
 * inside a sentence. Nothing here strips or adds one.
 */
const POPUP_ICON: Record<PopupEventKey, typeof BellOutlined> = {
  // firsttime
  signup: BulbOutlined,
  xero_connected: ApiOutlined,
  first_meeting_created: PlusOutlined,
  first_agenda_created: FileTextOutlined,
  first_analysis_generated: BarChartOutlined,
  first_notes_added: FileTextOutlined,
  branding_updated: GearOutlined,
  avatar_setup: CheckCircleOutlined,
  first_export: FileTextOutlined,
  first_meeting_completed: CheckCircleOutlined,
  first_recommendation_edited: FileTextOutlined,
  // everyuse
  login_welcome: BellOutlined,
  xero_connected_again: ApiOutlined,
  xero_sync_failed: CloseCircleOutlined,
  notes_added: FileTextOutlined,
  analysis_generated: BarChartOutlined,
  meeting_first_export: CheckCircleOutlined,
  meeting_tomorrow: ClockCircleOutlined,
  // inactivity
  inactive_7d: ClockCircleOutlined,
  inactive_30d: ClockCircleOutlined,
  // milestones
  firm_analysis_3: StarOutlined,
  firm_analysis_10: StarOutlined,
  firm_analysis_25: StarOutlined,
  firm_meetings_3: StarOutlined,
  teammate_first_analysis: StarOutlined,
  firm_second_meeting: StarOutlined,
  unknown: BellOutlined,
}

/**
 * The API's `createdAt` is normalised to an explicit-offset ISO string by the
 * adapter, so this is a plain relative format. It re-renders with the tab, so a
 * bubble that says "a few seconds ago" ages correctly while the panel is open.
 */
function relativeWhen(createdAt: string): string {
  if (!createdAt) return ''
  const parsed = dayjs(createdAt)
  return parsed.isValid() ? parsed.fromNow() : ''
}

function PopupSettingsView() {
  const { state, actions } = useAssistant()

  return (
    <div className="space-y-4">
      <Button size="small" icon={<LeftOutlined />} onClick={() => actions.setPopupSettingsOpen(false)}>
        Pop-ups
      </Button>

      <div className="text-[9.5px] font-extrabold uppercase tracking-[0.12em] text-gray-400">
        What Preppy may interrupt you for
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
        {state.popupSettings.map((setting, index) => (
          <div
            key={setting.key}
            className={`flex items-center gap-3 py-2.5 ${
              index === state.popupSettings.length - 1 ? '' : 'border-b border-gray-200'
            }`}
          >
            <div className="flex-1">
              <div className="text-xs text-gray-800">{setting.name}</div>
              <div className="text-[10.5px] text-gray-400">{setting.delivery}</div>
            </div>
            <Switch
              size="small"
              checked={setting.on}
              onChange={() => actions.togglePopupSetting(setting.key)}
              aria-label={`${setting.name} pop-ups`}
            />
          </div>
        ))}
      </div>

      <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-500">
        Inactivity nudges also go to email because you are, by definition, not here to see them.
        Everything else stays in the app.
      </p>
    </div>
  )
}

export function PopupsTab() {
  const { state, actions } = useAssistant()

  if (state.popupSettingsOpen) return <PopupSettingsView />

  const visible = state.popups.filter((popup) => (state.popupFilter === 'unread' ? popup.unread : true))

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5">
        <Button
          size="small"
          shape="round"
          type={state.popupFilter === 'all' ? 'primary' : 'default'}
          className={state.popupFilter === 'all' ? 'feature-button-solid' : ''}
          onClick={() => actions.setPopupFilter('all')}
        >
          All
        </Button>
        <Button
          size="small"
          shape="round"
          type={state.popupFilter === 'unread' ? 'primary' : 'default'}
          className={state.popupFilter === 'unread' ? 'feature-button-solid' : ''}
          onClick={() => actions.setPopupFilter('unread')}
        >
          Unread{state.unreadCount ? ` · ${state.unreadCount}` : ''}
        </Button>
        <Button size="small" shape="round" className="ml-auto" onClick={actions.markAllPopupsRead}>
          Mark all read
        </Button>
        <Button
          size="small"
          shape="circle"
          icon={<ControlOutlined />}
          aria-label="Pop-up settings"
          title="What Preppy may interrupt you for"
          onClick={() => actions.setPopupSettingsOpen(true)}
        />
      </div>

      {visible.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nothing waiting. Preppy will speak up when something changes."
        />
      ) : (
        visible.map((popup) => {
          const Icon = POPUP_ICON[popup.eventKey] ?? BellOutlined
          const showTourActions = isWelcomePopup(popup) && !state.tourSeen
          const route = popupTargetRoute(popup)

          // The card navigates where the pop-up points, and marks itself read on
          // the way — one click, the obvious meaning. Entries with nowhere to go
          // still mark read; the first-run message keeps its buttons instead, so
          // it is never a button inside a button.
          const clickable = !showTourActions && (Boolean(route) || popup.unread)

          const openEntry = () => {
            if (popup.unread) actions.markPopupRead(popup.id)
            if (route) actions.openRoute(route)
          }

          // The card is a div wrapping sibling buttons rather than a button
          // itself — "mark read without opening" has to live alongside the main
          // action, and a button inside a button is invalid markup that browsers
          // resolve unpredictably.
          return (
            <div
              key={popup.id}
              className={`flex w-full gap-3 rounded-xl border p-3 text-left transition-colors ${
                popup.unread
                  ? 'feature-soft-surface border-[var(--feature-border)]'
                  : 'border-gray-200 bg-white'
              } ${clickable ? 'hover:border-[var(--feature-primary)]' : ''}`}
            >
              <div className="feature-soft-surface feature-soft-text flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                <Icon />
              </div>

              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  disabled={!clickable}
                  onClick={openEntry}
                  aria-label={route ? `${popup.title} — open` : `Mark "${popup.title}" as read`}
                  className={`block w-full border-0 bg-transparent p-0 text-left ${
                    clickable ? 'cursor-pointer' : 'cursor-default'
                  }`}
                >
                  <span className="flex items-baseline justify-between gap-2.5">
                    <span className="text-[12.5px] font-semibold leading-snug text-gray-900">
                      {isWelcomePopup(popup) && (
                        <Tag color="blue" className="mr-1.5 align-middle">
                          First time here
                        </Tag>
                      )}
                      {popup.title}
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-[10.5px] text-gray-400">
                      {relativeWhen(popup.createdAt)}
                    </span>
                  </span>
                  <span className="mt-1 block text-[11.5px] leading-relaxed text-gray-600">
                    {popup.body}
                  </span>
                </button>

                {showTourActions && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Button size="small" type="primary" className="feature-button-solid" onClick={actions.takeTour}>
                      Show me around
                    </Button>
                    <Button size="small" onClick={actions.declineTour}>
                      I will explore
                    </Button>
                  </div>
                )}

                {/* Reading without being taken anywhere needs its own control,
                    now that the card itself navigates. */}
                {popup.unread && route && !showTourActions && (
                  <Button
                    size="small"
                    type="link"
                    className="mt-1 h-auto px-0 text-[11px]"
                    onClick={() => actions.markPopupRead(popup.id)}
                  >
                    Mark read without opening
                  </Button>
                )}
              </div>

              <Button
                size="small"
                type="text"
                shape="circle"
                icon={<DeleteOutlined />}
                aria-label={`Delete "${popup.title}"`}
                title="Delete this pop-up"
                className="shrink-0 self-start text-gray-300 hover:text-gray-600"
                onClick={() => actions.hidePopup(popup.id)}
              />
            </div>
          )
        })
      )}

      <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-500">
        Every trigger pops in the corner as it happens — this tab is the history. Anything you switch
        off in settings stops popping entirely; it does not just go quiet.
      </p>
    </div>
  )
}
